<template>
    <div>
        <div>
            <p class="direction text-body-1">
                <span class="direction-name">{{ visit.entity.name }}</span>
                <v-spacer/>
                <span v-if="visit.entitySpecialty" class="success--text">{{ visit.entitySpecialty.name }}</span>
                <v-btn
                    v-if="useVisitOrderRequest"
                    icon
                    small
                    class="ms-1"
                    @click.stop="fetchAndShowOrder"
                >
                    <v-icon color="primary">mdi-help-circle-outline</v-icon>
                </v-btn>
            </p>
        </div>

        <div class="d-flex align-center">
            <!-- deleteFab -->
            <v-fab-transition>
                <v-btn fab absolute left v-show="deleteFab" dark small text color="red darken-2">
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </v-fab-transition>

            <!-- card -->
            <v-card
				outlined
				width="90%"
				class="visit-card mx-auto"
				v-ripple="{class:'visit-ripple'}"
				v-touch.sync="{
				move: (e) => { !noActions ? touchMove(e) : null },
				end: (e) => { !noActions ? endOffset(e) : null }
				}"
				:style="'transform: translateX('+ touchSwipeOffset +'px)'"
				:disabled="disabled"
            >
                <v-card-text @click="$emit('on-click')" style="position: relative">
                    <!-- {{ typeof salesSystem }} -->
                    <!-- persons -->
                    <div v-if="visit.residentDoctorNames">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-account</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">persons</span>
                        </div>
                        <div>{{ visit.residentDoctorNames }}</div>
                    </div>

                    <!-- samples -->
                    <div>
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-pill</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">samples</span>
                        </div>
                        <div v-if="visit.materialAmounts?.length > 0">{{ samples }}</div>
                        <div v-else>No samples </div>
                    </div>

                    <!-- Unavailable Products -->
                    <div v-if="visit.unavailableMaterials?.length > 0" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-close</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Unavailable</span>
                        </div>
                        <div>{{ unavailableMaterials }}</div>
                    </div>

                    <!-- Competitors -->
                    <div v-if="visit.competitiveSamples?.length > 0" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-trending-up</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Competitors</span>
                        </div>
                        <div>{{ visit.competitiveSamples.join(', ') }}</div>
                    </div>

                    <!-- Core message -->
                    <div v-if="visit.coreMessage" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-note-outline</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Core message</span>
                        </div>
                        <div>{{ visit.coreMessage }}</div>
                    </div>

                    <!-- durationInMinutes -->
                    <div v-if="visit.durationInMinutes" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-clock-outline</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Duration</span>
                        </div>
                        <div>{{ $options.filters.formatNumber(visit.durationInMinutes, 3) }} min</div>
                    </div>

                    <!-- other materials table -->
                    <div v-if="visit.otherSamples" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-plus-minus-box</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">other materials</span>
                        </div>
                        <div>{{ visit.otherSamples }}</div>
                    </div>

                    <!-- notes -->
                    <div v-if="visit.notes" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-note-multiple-outline</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">notes</span>
                        </div>
                        <div>{{ visit.notes }}</div>
                    </div>

                    <!-- Objections -->
                    <div v-if="visit.objections" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-alert-circle-outline</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Objections</span>
                        </div>
                        <div>{{ visit.objections }}</div>
                    </div>

                    <!-- next call -->
                    <div v-if="visit.purpose" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-check</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Next Call</span>
                        </div>
                        <div>{{ visit.purpose }}</div>
                    </div>

                    <!-- center notes -->
                    <div v-if="visit.nextVisitNotes" class="mt-1">
                        <div>
                            <v-icon small class="primary--text text--lighten-2 me-1">mdi-note-multiple-outline</v-icon>
                            <span class="text-capitalize primary--text text--darken-1">Center Notes</span>
                        </div>
                        <div>{{ visit.nextVisitNotes }}</div>
                    </div>
                </v-card-text>
                
                <v-card-actions class="pt-0" >
                    <v-spacer/>

                    <v-btn
                        v-if="isSalesSystemEnabled && visit.payment === paymentMethod.debit && remainingAmountForDebit > 0"
                        text
                        color="primary"
                        @click="openAddPaymentDialog"
                    >
                        {{$T('inputs.add-payment')}}
                    </v-btn>

                    <v-btn
                        v-if="!noActions"
                        text
                        color="red lighten-1"
                        @click="$emit('on-swipe-right')"
                    >
                        delete
                    </v-btn>
                    
                    <v-btn
                        v-if="!noActions"
                        text
                        color="success"
                        @click="editVisit"
                    >
                        edit
                    </v-btn>

                    <!-- invoice button: shown only when usePrices (sales mode) is active -->
                    <v-btn
                        v-if="usePrices"
                        icon
                        color="blue darken-1"
                        @click="$emit('on-invoice', visit.id)"
                    >
                        print
                    </v-btn>
                </v-card-actions>
            </v-card>

            <v-dialog
                v-model="orderDialog"
                max-width="560"
            >
                <v-card>
                    <v-toolbar flat color="primary" height="50" dark>
                        <v-spacer/>
                        {{ $T('Visits.order-details') }}
                        <v-spacer/>
                    </v-toolbar>

                    <v-card-text class="pt-4">
                        <v-progress-circular
                            v-if="loadingOrder"
                            indeterminate
                            color="primary"
                            class="d-block mx-auto"
                        />

                        <template v-else>
                            <div v-if="orderDetails?.length">
                                <v-row
                                    v-for="(item, index) in orderDetails"
                                    :key="index"
                                    dense
                                    class="py-1"
                                >
                                    <v-col cols="8">
                                        {{ getMaterialById(item.materialId) ? getFullMaterialName(getMaterialById(item.materialId)) : item.materialId }}
                                    </v-col>
                                    <v-col cols="4" class="text-end">
                                        {{ item.amount }}
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-else class="text-center grey--text">
                                {{ $T('Visits.no-order-items') }}
                            </div>
                        </template>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer/>
                        <v-btn text color="primary" @click="orderDialog = false">
                            {{ $T('close') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog
                v-model="addPaymentDialog"
                max-width="560"
            >
                <v-card>
                    <v-toolbar flat color="primary" height="50" dark>
                        <v-spacer/>
                        {{$T('inputs.add-payment')}}
                        <v-spacer/>
                    </v-toolbar>

                    <v-card-text class="pt-4">
                        <v-sheet outlined rounded class="pa-3 payment-summary">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="text-body-2">{{$T('inputs.paid-amount')}}</span>
                                <span class="font-weight-bold">{{$options.filters.formatNumber(currentPaidAmount, 3)}}</span>
                            </div>
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="text-body-2">{{$T('inputs.remaining-amount')}}</span>
                                <span class="font-weight-bold error--text">{{$options.filters.formatNumber(remainingAmountForDebit, 3)}}</span>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                                <span class="text-body-2">{{$T('inputs.paid-amount-after-payment')}}</span>
                                <span class="font-weight-bold success--text">{{$options.filters.formatNumber(paidAmountAfterNewPayment, 3)}}</span>
                            </div>
                        </v-sheet>

                        <v-text-field
                            v-model.number="additionalPaymentAmount"
                            type="number"
                            dense
                            class="mt-3"
                            hide-spin-buttons
                            :label="$T('inputs.new-payment-amount')"
                            :rules="[
                                val => Number(val || 0) > 0 || $T('validation-messages.number'),
                                val => Number(val || 0) <= +remainingAmountForDebit || $T('messages.payment-amount-exceeds-remaining')
                            ]"
                        />
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer/>
                        <v-btn text color="success" :loading="paymentUpdateLoading" :disabled="paymentUpdateLoading" @click="applyAdditionalPayment">
                            {{$T('save')}}
                        </v-btn>
                        <v-btn text color="grey darken-1" :disabled="paymentUpdateLoading" @click="addPaymentDialog = false">
                            {{$T('close')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- EditFab -->
            <v-fab-transition>
                <v-btn fab absolute right v-show="EditFab" dark small text color="success darken-2">
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
            </v-fab-transition>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { useVisitOrderRequest } from '@/configs/general.configs'
import { paymentMethod } from '@/helpers/enums'
export default {
	props: [
		'disabled',
		'visit',
		'noActions',
		'usePrices',
	],

	data() {
		return {
			useVisitOrderRequest,
			paymentMethod,
			touchSwipeOffset: 0,
			maxOffsetAllowed: 30,
			isTouchDevice: true,

			snackbar: false,
			message: null,

			deleteFab: false,
			EditFab: false,
			orderDialog: false,
			visitOrder: null,
			loadingOrder: false,
			addPaymentDialog: false,
			additionalPaymentAmount: null,
			paymentUpdateLoading: false,
		}
	},

	methods: {
		// touch methods
		endOffset(e) {
			setTimeout(() => {
				if (this.touchSwipeOffset === this.maxOffsetAllowed) {
					this.$emit('on-swipe-right');
				} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
					this.editVisit();
				}
				this.deleteFab = false;
				this.EditFab = false;
				this.touchSwipeOffset = 0;
			}, 200);
		},

		touchMove(e) {
			const offset = e.touchmoveX - e.touchstartX;
			this.touchSwipeOffset = offset > -this.maxOffsetAllowed && offset < this.maxOffsetAllowed ? offset : Math.sign(offset) * this.maxOffsetAllowed;

			if (this.touchSwipeOffset === this.maxOffsetAllowed) {
				this.deleteFab = true;
				this.EditFab = false;
			} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
				this.deleteFab = false;
				this.EditFab = true;
			} else {
				this.deleteFab = false;
				this.EditFab = false;
			}
		},

		// component methods
		getFullMaterialName(material) {
			const details = [
				this.getMaterialFormById(material.materialFormId).name,
				material.dose,
				material.strength
			];

			return `${material.name} (${details.filter(c => c != null).join(' ')})`;
		},

		editVisit() {
			const visitDate = window.moment(this.visit.createdAt);
			const today = window.moment();

			if (today.diff(visitDate, 'days') <= 1) {
				this.$emit('on-edit', this.visit.id)
			} else {
				this.$emit('on-edit-prevent', 'You can ONLY EDIT today visits')
			}
		},

		fetchAndShowOrder() {
			console.log('fetchAndShowOrder called with visit id:', this.visit.id, (this.useVisitOrderRequest === false || !this.visit.id));
			if (this.useVisitOrderRequest === false && !this.visit.id) return

			this.orderDialog = true
			this.loadingOrder = true
			this.visitOrder = null

			this.$store.dispatch('order/fetchOrderByVisitId', { visitId: this.visit.id })
				.then((response) => {
					this.visitOrder = response?.order || response || null
				})
				.catch(() => {
					this.visitOrder = null
				})
				.finally(() => {
					this.loadingOrder = false
				})
		},

		formatDeliveryDate(order) {
			const date = order?.orderDeliveryDate || order?.deliverDate
			return date ? window.moment(date).format('DD/MM/YYYY') : 'N/A'
		},

		openAddPaymentDialog() {
			this.additionalPaymentAmount = null
			this.addPaymentDialog = true
		},

		buildVisitUpdatePayload(newAmountReceived) {
			const unavailableMaterialIds = (this.visit.unavailableMaterials || [])
				.map(name => this.getMaterialByName(name)?.id)
				.filter(Boolean)

			return {
				id: this.visit.id,
				entityId: this.visit.entity?.id || this.visit.entityId,
				coreMessage: this.visit.coreMessage,
				durationInMinutes: this.visit.durationInMinutes,
				otherSamples: this.visit.otherSamples,
				notes: this.visit.notes,
				objections: this.visit.objections,
				nextVisitNotes: this.visit.nextVisitNotes,
				purpose: this.visit.purpose,
				materialAmounts: this.visit.materialAmounts || [],
				unavailableMaterialIds,
				competitiveSamples: this.visit.competitiveSamples || [],
				residentDoctorNames: this.visit.residentDoctorNames || null,
				payment: this.visit.payment,
				amountReceived: newAmountReceived,
				createdAt: this.visit.createdAt,
			}
		},

		applyAdditionalPayment() {
			const paymentToAdd = Number(this.additionalPaymentAmount || 0)
			if (!paymentToAdd) return

			if (paymentToAdd > this.remainingAmountForDebit) {
				this.$eventBus.$emit('show-snackbar', this.$T('messages.payment-amount-exceeds-remaining'), 'info')
				return
			}

			const newAmountReceived = this.currentPaidAmount + paymentToAdd
			const payload = this.buildVisitUpdatePayload(newAmountReceived)

			this.paymentUpdateLoading = true
			this.$store.dispatch('visits/update', payload)
				.then(() => {
					this.$eventBus.$emit('show-snackbar', this.$T('Visits.visit-is-updated'), 'success')
					this.$store.dispatch('profile/fetchBalance')
					this.$emit('on-payment-updated')
					this.addPaymentDialog = false
					this.additionalPaymentAmount = null
				})
				.finally(() => {
					this.paymentUpdateLoading = false
				})
		},
	},

	computed: {
		...mapState({
			salesSystem: state => state.configurations.systemConfigValue.properties?.find(c => c.key === 'salesSystem')?.value,
		}),

		isSalesSystemEnabled() {
			return this.salesSystem === true
		},

		visitTotal() {
			return (this.visit.materialAmounts || []).reduce((sum, item) => {
				const amount = Number(item?.amount || 0)
				const unitPrice = Number(item?.sellPrice || item?.price || 0)
				const discount = Number(item?.priceDiscount || item?.discount || 0)
				const gross = amount * unitPrice
				return sum + (gross - (discount / 100) * gross)
			}, 0)
		},

		currentPaidAmount() {
			return Number(this.visit.amountReceived || 0)
		},

		remainingAmountForDebit() {
			const remaining = this.visitTotal - this.currentPaidAmount
			return remaining > 0 ? remaining : 0
		},

		paidAmountAfterNewPayment() {
			return this.currentPaidAmount + Number(this.additionalPaymentAmount || 0)
		},

		orderDetails() {
			if (!this.visitOrder) return []

			const details = Array.isArray(this.visitOrder.orderDetail)
				? this.visitOrder.orderDetail
				: (Array.isArray(this.visitOrder.orderDetails) ? this.visitOrder.orderDetails : [])

			return details.filter(item => item && item.materialId && item.amount)
		},

		samples() {
			const samples = [];
			this.visit.materialAmounts.forEach(materialAmount => {
				const material = this.getMaterialById(materialAmount.materialId);
				const amount = materialAmount.amount;
				samples.push(this.getFullMaterialName(material) + ' (×' + amount + ')');
			});
			return samples.join(', ');
		},

		unavailableMaterials() {
			const materials = [];
			this.visit.unavailableMaterials.forEach(materialName => {
				const material = this.getMaterialByName(materialName);
				materials.push(this.getFullMaterialName(material));
			});
			return materials.join(', ');
		},

		...mapGetters({
			getMaterialById: 'materials/getMaterialById',
			getMaterialByName: 'materials/getMaterialByName',
			getMaterialFormById: 'materialForms/getMaterialFormById'
		})
	},

	mounted() {
		this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
	}
}
</script>

<style lang="scss">
@import '../scss/vars.scss';
.payment-summary {
	background: linear-gradient(180deg, #fafcfc 0%, #ffffff 100%);
	border-color: #d8e6e6 !important;
}

.visit-card{
    transition: transform .4s ease;
    overflow: hidden;
}
.visit-ripple{
    color: rgba($primary, .5);
}
.direction{
    display: flex;
    margin-bottom: .5rem;
    align-items: center;
    position: relative;
    
    &-name{
        display: flex;
        align-items: center;
        margin-left: 22px;

        &::before{
            content: '';
            display: inline-block;
            position: absolute;
            left: -18px;
            width: 30px;
            height: 8px;
            border-radius: 10px;

            background-color: $success;
        }

    }
}
</style>
